/**
 * @description
 * SelectOffers Component
 *
 * Displays and manages the list of offers available for a specific corporate.
 * - Fetches and displays corporate offers in a table
 * - Supports offer creation, editing, viewing, and deletion
 * - Allows selection of multiple offers with validity control
 * - Handles confirmation mode with pre-selected offers (read-only mode)
 */

import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
    //   Table,
    Spin,
    Checkbox,
    Modal,
    message,
    DatePicker,
    Dropdown,
} from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import CreateOffer from '../create-offer/create-offer';
import { Items } from '../../models';
import { TableComponent } from 'soxo-bootstrap-core';
import './select-offers.scss';
import AppLoader from '../../components/app-loader';

const SelectOffers = ({ onChange, onSelectionChange, corporateDetails, showOnlySelected, initialSelectedOffers = [], confirmationMode = false }) => {
    const [offers, setOffers] = useState([]); // stores all available offers
    const [loading, setLoading] = useState(false); // loading state for data fetching
    const [selectedKeys, setSelectedKeys] = useState([]); // stores selected offers

    // modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create | edit | view
    const [editingRecord, setEditingRecord] = useState(null);

    const { RangePicker } = DatePicker;
    // Below variable should go to the store and be standardised for use accross the project
    let isDesktop = window.innerWidth >= 950;

    // mapping for discount type labels
    const discountTypeLabels = {
        N: 'None',
        D: 'Discount (%)',
        RA: 'Based on other rate type',
        DA: 'Discount % based on other rate type',
    };

    //  Fetch offers
    // Fetch offers and store in state
    const loadOffers = async () => {
        setLoading(true);
        try {
            const response = await Items.getOffers(corporateDetails.cc_code);
            const offerList = response?.result?.data || [];
            // format response for table display
            const formatted = offerList.map((item, index) => {
                const details = item.other_details || {};
                return {
                    key: item.code || index + 1,
                    code: item.code,
                    description: item.description,
                    discountType: item.discount_type,
                    sl: item.sl_no,
                    remarks: details.remarks || item.remarks,
                    discount: item.discount,
                    validity: [details.valid_from ? moment(details.valid_from) : null, details.valid_to ? moment(details.valid_to) : null],
                    offerAppliedCorporate: item.offer_applied_corporate,
                    isExpired: item.is_expired,
                    active: item.active,
                };
            });

            setOffers(formatted);

            // auto-select offers already applied to corporate
            const preSelected = formatted.filter((o) => o.offerAppliedCorporate).map((o) => o.key);
            setSelectedKeys(preSelected);
            onSelectionChange?.(formatted.filter((o) => preSelected.includes(o.key)));
        } catch (err) {
            console.error(err);
            message.error('Failed to load offers');
        } finally {
            setLoading(false);
        }
    };

    // Disable past dates in calendar
    const disablePastDates = (current) => {
        return current && current < moment().startOf('day');
    };

    /**
     * @description
     * Initialize offers when in confirmation mode.
     *
     * This effect runs **only once** (empty dependency array `[]`) when the component mounts.
     *
     * - It checks if the component is in `confirmationMode`, which means we're viewing
     *   or confirming existing offers rather than editing or creating new ones.
     * - If `initialSelectedOffers` are passed as props (usually pre-selected offers from backend),
     *   it formats them into the same structure used by the offers table.
     * - Each offer is normalized and assigned:
     *   - A **stable key** (from `key`, `code`, or fallback index)
     *   - Core attributes (`code`, `description`, `discountType`, etc.)
     *   - A parsed `validity` range converted to Moment objects (for Ant Design `RangePicker`)
     *   - Boolean flags for `isExpired` and `offerAppliedCorporate`
     * - Once formatted:
     *   - Offers are stored in the component state via `setOffers()`
     *   - Keys are extracted and stored in `setSelectedKeys()` for checkbox management
     *   - The parent component (if provided) is notified via `onSelectionChange(formatted)`
     *
     * @effect
     * Runs only once on mount due to `[]` dependency.
     *
     * @dependencies
     * None — runs a single initialization when the component is first rendered.
     */

    useEffect(() => {
        if (confirmationMode && initialSelectedOffers?.length) {
            //  Step 1: Format the offers for internal table usage
            const formatted = initialSelectedOffers.map((o, index) => ({
                key: o.key || o.code || index, // unique and stable key
                code: o.code || '', // fallback to empty string if undefined
                description: o.description || '',
                discountType: o.discountType || 'N', // normalize discount type
                discount: o.discount || 0,
                sl: o.sl || index + 1, // ✅ include serial number
                active: o.active !== undefined ? o.active : true, // ✅ include active status
                remarks: o.remarks || '',
                // Format validity dates as Moment objects for DatePicker
                validity: Array.isArray(o.validity)
                    ? o.validity.map((d) => (d ? moment(d) : null)) // create Moment only once
                    : [null, null],
                isExpired: o.isExpired || o.is_expired || false,
                offerAppliedCorporate: o.offerAppliedCorporate || false,
            }));
            // Store formatted offers in component state
            setOffers(formatted);
            // Pre-select all offers (since confirmation mode shows selected ones)
            setSelectedKeys(formatted.map((o) => o.key));
            // Notify parent (if callback provided) with selected offers
            onSelectionChange?.(formatted);
        }
    }, []);

    // Update checkbox selection
    const handleCheckboxChange = (record, checked) => {
        const updatedKeys = checked ? [...selectedKeys, record.key] : selectedKeys.filter((k) => k !== record.key);
        setSelectedKeys(updatedKeys);

        const updatedSelected = offers.filter((o) => updatedKeys.includes(o.key));
        onSelectionChange?.(updatedSelected);
    };

    const handleValidityChange = (record, dates) => {
        setOffers((prev) => prev.map((o) => (o.key === record.key ? { ...o, validity: dates || [null, null] } : o)));

        // Always update parent with selected offers
        const updatedSelected = offers.map((o) => (o.key === record.key ? { ...o, validity: dates } : o)).filter((o) => selectedKeys.includes(o.key));

        onSelectionChange?.(updatedSelected);
    };

    // reload offers only in normal (non-confirmation) mode

    useEffect(() => {
        if (!confirmationMode) {
            loadOffers();
        }
    }, [confirmationMode]);

    // remove offer from corporate

    const handleDelete = (record) => {
        // if (record.offerAppliedCorporate) {
        //   message.warning("This offer is applied to corporate and cannot be deleted.");
        //   return;
        // }

        Modal.confirm({
            title: 'Are you sure you want to remove this offer from corporate?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            async onOk() {
                try {
                    const res = await Items.removedOffer(corporateDetails.cc_code, record.code);
                    if (res?.result?.success) {
                        message.success('Offer deleted successfully');
                        loadOffers();
                    } else {
                        message.warning(res?.result?.message || 'Failed to delete');
                    }
                } catch {
                    message.error('Error deleting offer');
                }
            },
        });
    };
    //  Open modal with create offer
    const openModal = (mode, record = null) => {
        setModalMode(mode);
        setEditingRecord(record);
        setIsModalVisible(true);
    };

    console.log('offfffer', offers);

    const dataSource = showOnlySelected ? offers.filter((o) => selectedKeys.includes(o.key)) : offers;

    //  Table columns for offerslist
    const columns = [
        {
            title: '',
            dataIndex: 'checkbox',
            width: 40,
            align: 'center',

            render: (_, record) => (
                <Checkbox
                    checked={selectedKeys.includes(record.key)}
                    disabled={record.offerAppliedCorporate}
                    onChange={(e) => handleCheckboxChange(record, e.target.checked)}
                />
            ),
        },
        { title: 'Code', dataIndex: 'code', width: 100 },
        { title: 'Description', dataIndex: 'description', width: 150},
        {
            title: 'Discount Type',
            dataIndex: 'discountType',
            width: 180,
            render: (value) => discountTypeLabels[value] || value,
        },
        { title: 'Discount (%)', dataIndex: 'discount', width: 100, align: 'center' },
        // { title: 'Remarks', dataIndex: 'remarks', width: 300 },
        // {
        //     title: 'Ref. Rate Type',
        //     dataIndex: 'refRateType',
        //     width: 20,
        //     align: 'center',
        // },
        {
            title: 'Validity',
            dataIndex: 'validity',
            width: 290,

            render: (_, record) => (
                <RangePicker
                    style={{ width: 250 }} // or '100%' if you want full cell width
                    value={record.validity || [null, null]} // no map needed
                    format="DD/MM/YYYY"
                    disabledDate={disablePastDates} // ✅ prevent past date selection
                    onChange={(dates) => handleValidityChange(record, dates)}
                />
            ),
        },
        {
            title: 'Expired',
            dataIndex: 'isExpired',
            width: 100,
            align: 'center',
            render: (v) => <span style={{ color: v ? 'red' : 'green', fontWeight: 500 }}>{v ? 'Expired' : 'Active'}</span>,
        },
        {
            title: 'Action',
            key: 'action',
            width: 90,
            align: 'center',

            render: (_, record) => {
                const menuItems = [
                    {
                        key: 'view',
                        label: 'View',
                        icon: <EyeOutlined />,
                        onClick: () => openModal('view', record),
                    },
                    {
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditOutlined />,
                        onClick: () => openModal('edit', record),
                    },
                    {
                        key: 'delete',
                        label: 'Delete',
                        icon: <DeleteOutlined />,
                        onClick: () => handleDelete(record),
                    },
                ];
                return (
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                        <MoreOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <div>
            <div className="header-section" style={{ display: 'flex', justifyContent: 'end', marginBottom: '12px' }}>
                <CreateOffer
                    mode="create"
                    visible={isModalVisible && modalMode === 'create'}
                    onClose={() => setIsModalVisible(false)}
                    reload={loadOffers}
                />
                <button className="ant-btn ant-btn-primary" onClick={() => openModal('create')}>
                    <PlusOutlined /> Create Offer
                </button>
            </div>

            {loading ? (
                <AppLoader visible={loading} />
            ) : (
                <TableComponent scroll={{ x: true }} columns={columns} dataSource={dataSource} pagination={false} rowKey="key" />
            )}

            {/* Shared modal for Edit / View */}
            {isModalVisible && modalMode !== 'create' && (
                <CreateOffer
                    mode={modalMode}
                    record={editingRecord}
                    visible={isModalVisible}
                    onClose={() => setIsModalVisible(false)}
                    reload={loadOffers}
                />
            )}
        </div>
    );
};

export default SelectOffers;
